import React, { useState } from 'react';
import { CreditCard, CheckCircle, XCircle, AlertCircle, Loader2, Calendar, DollarSign, User } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';

const Subscription: React.FC = () => {
  const { 
    subscription, 
    isActive, 
    isLoading, 
    error, 
    plans,
    activateSubscription,
    deactivateSubscription,
    syncResourceSubscription,
    fetchStripeDetails
  } = useSubscription();

  const [activatingPlan, setActivatingPlan] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loadingStripeDetails, setLoadingStripeDetails] = useState(false);

  const handleActivatePlan = async (planId: string) => {
    setActivatingPlan(planId);
    try {
      const success = await activateSubscription(planId);
      if (success) {
        // Find the plan name for the success message
        const selectedPlan = plans.find(plan => plan.id === planId);
        console.log(`🎉 Successfully activated ${selectedPlan?.name} subscription!`);
        // You can add a toast notification here later
      }
    } finally {
      setActivatingPlan(null);
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      await deactivateSubscription();
    } finally {
      setDeactivating(false);
    }
  };

  const handleSyncResources = async () => {
    setSyncing(true);
    try {
      const success = await syncResourceSubscription();
      if (success) {
        console.log('✅ Successfully synced resource subscription status');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleRefreshStripeDetails = async () => {
    setLoadingStripeDetails(true);
    try {
      await fetchStripeDetails();
    } finally {
      setLoadingStripeDetails(false);
    }
  };

  const getStatusIcon = () => {
    if (isLoading) return <Loader2 className="h-5 w-5 animate-spin text-gray-400" />;
    if (isActive) return <CheckCircle className="h-5 w-5 text-green-500" />;
    return <XCircle className="h-5 w-5 text-red-500" />;
  };

  const getStatusText = () => {
    if (isLoading) return 'Loading...';
    if (isActive) return 'Active';
    return 'Inactive';
  };

  const getStatusColor = () => {
    if (isLoading) return 'text-gray-500';
    if (isActive) return 'text-green-600';
    return 'text-red-600';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  return (
    <div 
      className="min-h-screen bg-gray-100 p-2 sm:p-4"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 pb-24">
        {/* Current Subscription Status */}
        <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200/50">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 tracking-tight">Current Subscription</h2>
          </div>
          <div className="p-4 sm:p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
                  <span className="text-red-700 font-medium">Error: {error}</span>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {/* Status Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CreditCard className="h-6 w-6 text-gray-600" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Subscription Status</h3>
                      <p className="text-sm text-gray-500">Current subscription level and status</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getStatusIcon()}
                    <span className={`font-semibold ${getStatusColor()}`}>
                      {getStatusText()}
                    </span>
                  </div>
                </div>

                {subscription && (
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-500">Status</p>
                      <p className="text-lg font-semibold text-gray-900 capitalize">
                        {subscription.subscription_status}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-500">Last Checked</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {subscription.subscription_last_checked 
                          ? new Date(subscription.subscription_last_checked).toLocaleDateString()
                          : 'Never'
                        }
                      </p>
                    </div>
                    {subscription.active_plan_id && (
                      <div className="bg-gray-50 rounded-lg p-4 sm:col-span-2">
                        <p className="text-sm font-medium text-gray-500">Active Plan</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {plans.find(plan => plan.id === subscription.active_plan_id)?.name || 'Unknown Plan'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Stripe Subscription Details */}
                {subscription?.stripeDetails && (
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-blue-900">Stripe Subscription Details</h4>
                      <button
                        onClick={handleRefreshStripeDetails}
                        disabled={loadingStripeDetails}
                        className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-lg hover:bg-blue-200 transition-all duration-200 disabled:opacity-50"
                      >
                        <Loader2 className={`h-3 w-3 mr-1 ${loadingStripeDetails ? 'animate-spin' : ''}`} />
                        {loadingStripeDetails ? 'Refreshing...' : 'Refresh'}
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <DollarSign className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-700">Amount</p>
                          <p className="text-lg font-semibold text-blue-900">
                            {formatCurrency(subscription.stripeDetails.amount, subscription.stripeDetails.currency)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Calendar className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-700">Renewal Date</p>
                          <p className="text-lg font-semibold text-blue-900">
                            {formatDate(subscription.stripeDetails.renewalDate)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <User className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-700">Customer Email</p>
                          <p className="text-lg font-semibold text-blue-900">
                            {subscription.stripeDetails.customerEmail}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <CreditCard className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-700">Billing Cycle</p>
                          <p className="text-lg font-semibold text-blue-900 capitalize">
                            {subscription.stripeDetails.interval}ly
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-blue-700 font-medium">Current Period Start</p>
                          <p className="text-blue-900">{formatDate(subscription.stripeDetails.currentPeriodStart)}</p>
                        </div>
                        <div>
                          <p className="text-blue-700 font-medium">Current Period End</p>
                          <p className="text-blue-900">{formatDate(subscription.stripeDetails.currentPeriodEnd)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isActive && (
                  <div className="mt-6">
                    <button
                      onClick={handleDeactivate}
                      disabled={deactivating}
                      className="inline-flex items-center px-4 py-2 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-all duration-200 disabled:opacity-50"
                    >
                      {deactivating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Deactivating...
                        </>
                      ) : (
                        'Deactivate Subscription'
                      )}
                    </button>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* Available Plans */}
        <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200/50">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 tracking-tight">Available Plan</h2>
          </div>
          <div className="p-4 sm:p-8">
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 max-w-md mx-auto">
              {plans.map((plan) => (
                <div key={plan.id} className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">${plan.price}</div>
                      <div className="text-sm text-gray-500">per {plan.interval}</div>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleActivatePlan(plan.id)}
                    disabled={isLoading || activatingPlan === plan.id || (isActive && subscription?.active_plan_id === plan.id)}
                    className={`w-full inline-flex items-center justify-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-200 active:scale-95 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                      isActive && subscription?.active_plan_id === plan.id
                        ? 'text-green-700 bg-green-50 border border-green-200'
                        : 'text-white bg-purple-700 hover:bg-purple-800'
                    }`}
                  >
                    {activatingPlan === plan.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Activating...
                      </>
                    ) : isActive && subscription?.active_plan_id === plan.id ? (
                      '✓ Currently Active'
                    ) : (
                      'Activate Plan'
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription; 